import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Flashbar, SpaceBetween, Spinner, Tabs, TabsProps } from '@amzn/awsui-components-react-v3';
import { getCatalogForIdInfo } from 'src/components/catalog/common';
import {
  fetchTemplatesForResourceId,
  getOmniOncallLink,
  isDataLakeAdmin,
  isDGSAdmin,
  keyForCatalogNameMap,
} from 'src/commons/common';
import { ColumnsTable } from 'src/components/catalog/datasetdetail/tabs/ColumnsTable';
import { DatasetTagList } from 'src/components/catalog/datasetdetail/tabs/DatasetTagList';
import { listDataSets, syncDataSets } from 'src/api/catalog';
import { getResourceTags } from 'src/api/permissions';
import { AccessSelectModal } from 'src/components/catalog/datasetdetail/accessSelectModal/accessSelectModal';
import { DatasetEditModal } from 'src/components/catalog/datasetdetail/datasetEdit';
import { isCommercialPartition } from 'src/commons/validationUtils';
import { createDatasetDetailLink, createGroupPermissionLFLink, Page } from 'src/routes';
import {
  ACCESS_PROVIDER_GALAXI,
  CONTACT_PRIMARY_OWNER_FORM_NAME,
  DATAZONE_TYPE,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  FGA_POLICY_TYPE_PREFIX,
  GALAXI_ACCESS_TYPE,
  GALAXI_TYPE,
  IAM_ACCESS_ITEM,
  RESOURCE_ITEM,
  TABLE_CONTENT_TYPE,
  TABLE_STATUS_DEPRECATED,
  TAG_ITEM,
} from 'src/commons/constants';
import { DataConsumersTable } from 'src/components/permissions/myBaselining/dataConsumersTable';
import MetadataDetails from 'src/components/workspaces/common/metadataDetails';
import { AdvisoriesForResourceTable } from 'src/components/dataadvisory/listAdvisoriesPage/advisoriesForResource';
import { TemplatesForResourceDetailsTable } from 'src/components/templates/TemplatesForResourceDetailsTable';
import { enableAdvisories, galaxiServiceIntegrated } from 'src/api/config';
import { DataQuality } from 'src/components/catalog/dataquality/dataQuality';
import {
  checkIfTagIsAlreadyConsumedAndGetPublisherTag,
  fetchAllGlobalGovernanceTagsInHierarchy,
  generateMetaDataMap,
  getAllTableAccessItem,
  getConsumedPermissionsOfFineGrainPolicy,
  getCtiFromMetadataMap,
  getDistinctObjects,
  getGlossariesForResource,
  getSingleTableAccessDataPermission,
  isMPData,
  lakeFormationIcon,
  loadDZTableDataPermission,
  loadTableDataPermission,
  metadataMapHasCTI,
  redshiftIcon,
} from 'src/components/workspaces/common/common';
import { signedInWithWorkspace } from 'src/components/dataadvisory/constants';
import IamConsumersTable from 'src/components/catalog/datasetdetail/tabs/iamConsumers';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { flattenItem } from 'src/components/catalog/browsedatasets';
import {
  DataPermissionList,
  FGAPList,
  GalaxiGroup,
  GetResourceTagsResult,
  TagsList,
  TagStructure,
  TemplatesList,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';
import { BUSINESS_GLOSSARY_TYPE } from 'src/components/metadata/constants';
import { CartAccessItem } from 'src/components/common/checkout/common';
import { handleListDataQualityMetrics } from 'src/components/catalog/dataquality/common';
import {
  CatalogInfo,
  DataQualityList,
  GetDataSetRequest,
  GetDataSetResponse,
  GetDataSetResponseStructure,
  GlossaryDetailsList,
  SyncDataSetDetailRequest,
  SyncDataSetDetailResponse,
} from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import {
  generateArnFromId,
  generateHcGlueDatabaseId,
  generateHcRedshiftSchemaId,
  getHybridCatalogResourceArnFromHybridCatalogId,
  getIdInfoFromId,
  IdInfo,
} from 'src/components/utils/arnUtil';
import {
  createPermissionDropdownItemForDataZone,
  createPermissionDropdownItemForFgap,
  createPermissionDropdownItemForGalaxi,
  createPermissionDropdownItemForSingleTable,
  createPermissionDropdownItemForTag,
  fetchFineGrainPoliciesOnDataset,
  PermissionDropdownOption,
  resourceItemForConsumerTable,
} from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { FineGrainPoliciesTable } from 'src/components/catalog/datasetdetail/tabs/fineGrainPoliciesTable';
import { DatasetDetailsContainer } from 'src/components/catalog/datasetdetail/tabs/datasetDetailsContainer';
import { DetailsPageHeader } from 'src/components/catalog/datasetdetail/datasetDetailPageHeader';
import { ClassificationTab } from 'src/components/catalog/datasetdetail/tabs/ClassificationTab';
import Tab = TabsProps.Tab;
import { getBaseNodeIdV2, LineageDetailV2, useLineageAvailable } from 'src/components/lineagev2';

export interface WorkspaceDatasetDetailProps extends CommonPageProps {
  setActiveDatasetName: any;
  cartItemIds: any[];
  addToCart: (items: CartAccessItem[]) => void;
  groups: any[];
  userInfo: any;
  cartItems: [];
  galaxiGroups?: GalaxiGroup[];
}

export const DatasetDetails = (props: WorkspaceDatasetDetailProps) => {
  const [dataset, setDataset] = useState<GetDataSetResponseStructure>(undefined);
  const [editDataSetModalVisible, setEditDataSetModalVisible] = useState<boolean>(false);
  const [accessSelectVisible, setAccessSelectVisible] = useState<boolean>(false);
  const [catalogName, setCatalogName] = useState<string>(undefined);
  const [CTI, setCTI] = useState<string>(undefined);
  const [notifications, setNotifications] = useState([]);
  const [mostRecentOption, setMostRecentOption] = useState(undefined);
  const [option, setOption] = useState(undefined);
  const [tags, setTags] = useState<TagsList>([]);
  const [fgaPolicies, setFgaPolicies] = useState<FGAPList>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [getDatasetLoading, setGetDatasetLoading] = useState<boolean>(true);
  const [getDatasetFailed, setGetDatasetFailed] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [templates, setTemplates] = useState<TemplatesList>(undefined);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [businessGlossaries, setBusinessGlossaries] = useState<GlossaryDetailsList>(undefined);
  const [governanceTags, setGovernanceTags] = useState<GlossaryDetailsList>(undefined);
  const [groupLevelDataPermissionList, setGroupLevelDataPermissionList] = useState<DataPermissionList>([]);
  const [selectedPermissionType, setSelectedPermissionType] = useState<string>(undefined);
  const [alreadyHasDataZoneAccess, setAlreadyHasDataZoneAccess] = useState<boolean>(false);

  const [fgaAccessAvailable, setFgaAccessAvailable] = useState<boolean>(false);
  const [tagAccessAvailable, setTagAccessAvailable] = useState<boolean>(false);
  const [resourceAccessAvailable, setResourceAccessAvailable] = useState<boolean>(false);
  const [galaxiAccessAvailable, setGalaxiAccessAvailable] = useState<boolean>(false);

  const [permissionDropdownOptions, setPermissionDropdownOptions] = useState<PermissionDropdownOption[]>([]);

  // Lineage
  const [loadingBaseNodeId, setLoadingBaseNodeId] = useState(true);
  const [lineageBaseNodeId, setLineageBaseNodeId] = useState<string>(undefined);
  const { lineageEnabled, loadingLineage } = useLineageAvailable(lineageBaseNodeId);

  // Data quality
  const [dataQualityMetrics, setDataQualityMetrics] = useState<DataQualityList>([]);
  const [loadingDataQuality, setLoadingDataQuality] = useState<boolean>(true);
  const [dataQualityNotifications, setDataQualityNotifications] = useState([]);
  const [activeTabId, setActiveTabId] = useState<string>('Details');
  const datasetId = props.match.params.id;
  const idInfo: IdInfo = getIdInfoFromId(datasetId);
  const datasetArn = getHybridCatalogResourceArnFromHybridCatalogId(datasetId);

  const isGalaxi = ACCESS_PROVIDER_GALAXI == dataset?.AccessProvider;

  const SUBSCRIBE_TO_DATASET = 'Subscribe to dataset';

  // get dataset on page load
  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    fetchDataset();

    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTabId(tabParam);
    }
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeTabId !== 'Details') {
      url.searchParams.set('tab', activeTabId);
    } else {
      url.searchParams.delete('tab');
    }
    window.history.replaceState({}, '', url.toString());
  }, [activeTabId]);

  useEffect(() => {
    fetchTemplatesForDataset();
    fetchCatalogName();
  }, [datasetId]);

  useEffect(() => {
    if (!!catalogName && !!dataset) {
      fetchPublisherDataPermissions();
      fetchGlossariesAndGovernanceTags();
    }
  }, [catalogName, dataset]);

  useEffect(() => {
    if (!!dataset) {
      handleRefresh();
      fetchTags();
      fetchGalaxiAccessOptions();
    }
  }, [dataset]);

  const fetchDataset = async () => {
    setGetDatasetLoading(true);
    try {
      const listDataSetsRequest: GetDataSetRequest = {
        Filter: {
          IdList: [datasetId],
        },
      };
      const listDataSetsResponse: GetDataSetResponse = await listDataSets(listDataSetsRequest);
      setDataset(listDataSetsResponse.DataSetList[0]);
    } catch (e) {
      console.error('Failed to get dataset: ', e);
      setGetDatasetFailed(true);
    } finally {
      setGetDatasetLoading(false);
    }
  };

  const fetchCatalogName = async () => {
    let catalogName = props.catalogMap.get(keyForCatalogNameMap(idInfo)) ?? idInfo.catalogId;
    setCatalogName(catalogName);

    let metadataMap = await generateMetaDataMap(datasetArn);
    if (!metadataMapHasCTI(metadataMap.get(CONTACT_PRIMARY_OWNER_FORM_NAME)?.values)) {
      const catalog: CatalogInfo = await getCatalogForIdInfo(idInfo);
      setCatalogName(catalog.Name);
      setCTI(catalog?.CTI);
    } else {
      const [category, type, item] = getCtiFromMetadataMap(metadataMap.get(CONTACT_PRIMARY_OWNER_FORM_NAME)?.values);
      setCTI(category + '/' + type + '/' + item);
    }
    return catalogName;
  };

  const fetchGlossariesAndGovernanceTags = async () => {
    const datasetArn = generateArnFromId(datasetId);

    const fgaPolicyList = await fetchFineGrainPoliciesOnDataset(idInfo);
    setFgaPolicies(fgaPolicyList);
    if (fgaPolicyList?.length > 0) {
      setFgaAccessAvailable(true);
    }

    // Set up FGA drop down items
    if (signedInWithWorkspace(props)) {
      for (let fgaPolicy of fgaPolicyList) {
        let consumedPermission = await getConsumedPermissionsOfFineGrainPolicy(
          fgaPolicy,
          props.activeWorkspace,
          dataset,
        );
        addPermissionDropdownItem(
          createPermissionDropdownItemForFgap(fgaPolicy, catalogName, consumedPermission, props),
        );
      }
    }

    const allFGAPGlossaries = [];
    for (const fgaPolicy of fgaPolicyList) {
      // eg: arn:aws:dgs-gladstone:us-east-1:284697478245:glueLF/database/akaanj_lf_local_db/table/states/fine-grained-access-policy/fgap-8ed267a5a496475795340cdbfaa46f2
      const fgapArn = (`${datasetArn}` + `/fine-grained-access-policy/${fgaPolicy.id}`).replace(
        'dgs-hc',
        'dgs-gladstone',
      );
      const glossaryList = await getGlossariesForResource(fgapArn);
      allFGAPGlossaries.push(...glossaryList);
    }
    const fgapGovernanceTags = await fetchAllGlobalGovernanceTagsInHierarchy(allFGAPGlossaries);

    const allGlossaries = await getGlossariesForResource(datasetArn);
    setBusinessGlossaries(allGlossaries.filter((glossary) => BUSINESS_GLOSSARY_TYPE === glossary.GlossarySubType));
    const datasetGovernanceTags = await fetchAllGlobalGovernanceTagsInHierarchy(allGlossaries);
    const allGlobalGovernanceTags = fgapGovernanceTags
      ? getDistinctObjects([...fgapGovernanceTags, ...datasetGovernanceTags], 'GlossaryId')
      : datasetGovernanceTags;
    setGovernanceTags(allGlobalGovernanceTags);
  };

  const fetchPublisherDataPermissions = async () => {
    if (!signedInWithWorkspace(props)) return;

    let newPermissionDropdownOptions: PermissionDropdownOption[] = [];

    // Fetch existing data permissions
    let resourceId = `${idInfo.tableName}.${idInfo.databaseName}.${idInfo.catalogId}`;
    let permissionData = await getSingleTableAccessDataPermission(
      dataset,
      resourceId,
      props.activeWorkspace,
      props.cartItems,
    );
    let datasetPermissionId = permissionData?.permissionId;
    let isSingleDatasetConsumed = permissionData?.accountConsumed;
    let singleTableAccessDropDown = undefined;

    // Set up table-level dropdown item
    if (datasetPermissionId) {
      // Block MP data table level access
      if (!isMPData(idInfo.catalogId, idInfo.databaseName, idInfo.tableName)) {
        singleTableAccessDropDown = createPermissionDropdownItemForSingleTable(
          permissionData,
          resourceId,
          idInfo,
          catalogName,
          dataset,
        );
        newPermissionDropdownOptions.push(singleTableAccessDropDown);
        setResourceAccessAvailable(true);
      }
    }

    // Set up ALL_TABLES dropdown item
    let allTableDropDownItems = await getAllTableAccessItem(
      dataset,
      isSingleDatasetConsumed,
      permissionData?.iamPrincipals,
      catalogName,
      props.activeWorkspace,
      props.cartItems,
    );
    if (allTableDropDownItems && allTableDropDownItems.length !== 0) {
      // Remove duplicate single table request field
      for (let allTableItem of allTableDropDownItems) {
        // Block MP data db level access
        if (isMPData(allTableItem['catalogId'], allTableItem['databaseName'], allTableItem['datasetName'])) {
          console.log('Skipping all table access for MP dataset!');
          continue;
        }

        if (singleTableAccessDropDown !== undefined) {
          if (allTableItem['id'] !== singleTableAccessDropDown['id']) {
            newPermissionDropdownOptions.push(allTableItem);
            setResourceAccessAvailable(true);
          }
        } else {
          newPermissionDropdownOptions.push(allTableItem);
          setResourceAccessAvailable(true);
        }
      }
    }
    for (const permissionDropdownOption of newPermissionDropdownOptions) {
      addPermissionDropdownItem(permissionDropdownOption);
    }
  };

  const fetchGalaxiAccessOptions = async () => {
    // Add Galaxi permission options
    let galaxiDropdownItems = []; // items in the AccessSelectModal dropdown
    if (props.galaxiGroups != null && props.galaxiGroups?.length > 0) {
      console.log(`Populating permissions dropdowns with galaxiGroups:${props.galaxiGroups}`);
      const workspaceAccountId = props.activeWorkspace?.accountId;
      for (let galaxiGroup of props.galaxiGroups) {
        const item = createPermissionDropdownItemForGalaxi(galaxiGroup);
        if (workspaceAccountId) {
          const associations = item.accountAssociations?.filter((aa) => aa.accountId === workspaceAccountId) ?? [];
          if (associations.length === 0) continue;
          item.accountAssociations = associations;
        }
        galaxiDropdownItems.push(item);
      }
    }
    if (!signedInWithWorkspace(props)) {
      setPermissionDropdownOptions(galaxiDropdownItems);
      return;
    } else {
      for (const dropdownItem of galaxiDropdownItems) {
        addPermissionDropdownItem(dropdownItem);
      }
    }
    if (galaxiServiceIntegrated() && (props.galaxiGroups == null || props.galaxiGroups.length > 0)) {
      setGalaxiAccessAvailable(true);
    }
  };

  const fetchTags = async () => {
    const getTagsWithSimpleErrorHandling = async (id: string) => {
      try {
        const getTagsResponse: GetResourceTagsResult = await getResourceTags({
          resourceId: id,
          statusCustomerType: 'Active:Publisher',
        });
        return getTagsResponse.tags;
      } catch (e) {
        console.error('Error when getting tags: ', e);
        return [];
      }
    };
    let datasetTags: TagsList;
    let inheritedTags: TagsList;
    datasetTags = await getTagsWithSimpleErrorHandling(datasetId);
    if (dataset.ClusterIdentifier != null) {
      inheritedTags = await getTagsWithSimpleErrorHandling(generateHcRedshiftSchemaId(idInfo));
    } else {
      inheritedTags = await getTagsWithSimpleErrorHandling(generateHcGlueDatabaseId(idInfo));
    }
    let tagSet = new Set<TagStructure>();
    datasetTags.forEach((tag) => tagSet.add(tag));
    inheritedTags.forEach((tag) => tagSet.add(tag));
    const tagList: TagsList = [...tagSet];
    setTags(tagList);

    // Create dropdown options for tags
    if (!signedInWithWorkspace(props)) {
      setTagAccessAvailable(false);
      return tagList;
    }
    for (let tag of tagList) {
      let permissionData = await checkIfTagIsAlreadyConsumedAndGetPublisherTag(
        tag,
        dataset.Owners[0],
        dataset,
        props.activeWorkspace,
        props.cartItems,
      );
      let tagPermissionId = permissionData?.permissionId;
      let consumed = permissionData?.consumed;
      if (tagPermissionId !== undefined) {
        addPermissionDropdownItem(createPermissionDropdownItemForTag(tag, catalogName, dataset, permissionData));
        // if there are non-consumed tags, show "tags" as an option, but only once
        if (!consumed) {
          setTagAccessAvailable(true);
        }
      }
    }

    return tagList;
  };

  // Notify about deprecated dataset
  useEffect(() => {
    if (idInfo?.tableName) {
      props.setActiveDatasetName(idInfo?.tableName);
      if (dataset?.TableState == TABLE_STATUS_DEPRECATED) {
        notifyFailure('This dataset is deprecated. You cannot request access to it anymore.');
      }
    }
  }, [dataset]);

  const handleRefresh = async () => {
    setLoading(true);
    setSelectedPermissionType(undefined);
    try {
      // TODO: these remaining pieces can eventually be moved to hooks, if we want, to further improve page load time.
      // For group level LF
      if (
        dataset?.SupportedAccessTypes?.includes('LakeFormation') &&
        ACCESS_PROVIDER_GALAXI != dataset?.AccessProvider
      ) {
        let dataPermissionList = await loadTableDataPermission(props.activeGroup, dataset);
        setGroupLevelDataPermissionList(dataPermissionList);
      }

      // For DataZone:
      if (idInfo.dataSource.includes('DZ')) {
        const dzPermissions: DataPermissionList = await loadDZTableDataPermission(
          props.activeWorkspace.workspaceId,
          dataset,
        );
        setAlreadyHasDataZoneAccess(dzPermissions.length > 0);
        if (idInfo.dataSource == DZ_REDSHIFT_DATASOURCE_ID || idInfo.dataSource == DZ_GLUE_DATASOURCE_ID) {
          addPermissionDropdownItem(createPermissionDropdownItemForDataZone(props, idInfo, catalogName, dataset));
        }
      }

      // For lineage:
      const baseNodeId = await getBaseNodeIdV2(datasetId);
      setLineageBaseNodeId(baseNodeId);
      setLoadingBaseNodeId(false);

      // For data quality:
      const dataQuality: DataQualityList = await handleListDataQualityMetrics(
        datasetId,
        setLoadingDataQuality,
        setDataQualityNotifications,
      );
      setDataQualityMetrics(dataQuality);
      setLoadingDataQuality(false);
    } catch (err) {
      console.log('Failed to load dataset', err.message);
      setLoading(false);
    }
    setLoading(false);
  };

  const fetchTemplatesForDataset = async () => {
    setLoadingTemplates(true);
    let fetchedTemplates = await fetchTemplatesForResourceId(datasetId);
    setTemplates(fetchedTemplates);
    setLoadingTemplates(false);
  };

  // display "Consumers" section on UI
  const getDataConsumersTable = useMemo(() => {
    if (dataset) {
      const resource = resourceItemForConsumerTable(datasetId);
      return <DataConsumersTable {...props} resource={resource} />;
    }
  }, [dataset]);

  const notify = async (type, msg) => {
    setNotifications([
      {
        type: type,
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const notifySuccess = async (msg) => {
    setNotifications([
      {
        type: 'success',
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };
  const notifyFailure = async (msg) => {
    setNotifications([
      {
        type: 'error',
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const syncDataset = async () => {
    setSyncLoading(true);
    try {
      let syncRequest: SyncDataSetDetailRequest = {
        IdList: [datasetId],
      };
      const syncResponse: SyncDataSetDetailResponse = await syncDataSets(syncRequest);
      await fetchDataset();
      if (syncResponse.IdList?.[0]?.Message == 'Failed') {
        notifyFailure('Failed to sync dataset: ' + syncResponse.IdList?.[0]?.Reason);
      } else {
        notifySuccess('Successfully synced the dataset.');
      }
    } catch (err) {
      notifyFailure('Failed to sync dataset: ' + err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const openAccessSelectModal = (e) => {
    setSelectedPermissionType(e?.detail?.id);
    setAccessSelectVisible(true);
  };

  const addPermissionDropdownItem = (item: PermissionDropdownOption) => {
    setPermissionDropdownOptions((prevState) => {
      if (prevState.some((prevItem) => prevItem.id == item.id && prevItem.type == item.type)) {
        return prevState;
      }
      return [...prevState, item];
    });
  };

  const detailPageTabs = () => {
    let tabs: Tab[] = [
      {
        label: 'Details',
        id: 'Details',
        content: (
          <DatasetDetailsContainer
            dataset={dataset}
            option={option}
            mostRecentOption={mostRecentOption}
            cti={CTI}
            catalogName={catalogName}
            {...props}
          />
        ),
      },
      {
        label: 'Columns',
        id: 'Columns',
        content: (
          <ColumnsTable
            {...props}
            version={mostRecentOption?.item || option?.item}
            dataset={dataset}
            isPartitionKeysTable={false}
            notifyEditFailure={notifyFailure}
            handleFetchDataset={fetchDataset}
          />
        ),
      },
      {
        label: 'Partition keys',
        id: 'Partition keys',
        content: (
          <ColumnsTable
            {...props}
            version={mostRecentOption?.item || option?.item}
            dataset={dataset}
            isPartitionKeysTable={true}
            notifyEditFailure={notifyFailure}
            handleFetchDataset={fetchDataset}
          />
        ),
      },
      {
        label: (
          <span>
            Classification - <i>Preview</i>
          </span>
        ),
        id: 'Classification',
        content: (
          <ClassificationTab
            datasetId={datasetId}
            userOwnsDataset={userOwnsDataset()}
            version={mostRecentOption?.item || option?.item}
            onNotifySuccess={notifySuccess}
            onNotifyFailure={notifyFailure}
          />
        ),
      },
      {
        label: 'Data quality',
        id: 'Data quality',
        content: (
          <DataQuality
            setContentType={props.setContentType}
            dataQualityMetrics={dataQualityMetrics}
            setDataQualityMetrics={setDataQualityMetrics}
            loadingDataQuality={loadingDataQuality}
            setLoadingDataQuality={setLoadingDataQuality}
            notifications={dataQualityNotifications}
          />
        ),
        disabled: !dataQualityAvailable(),
        disabledReason: loadingDataQuality
          ? 'Loading...'
          : 'Data quality information is not available for this dataset.',
        contentRenderStrategy: 'eager',
      },
      {
        label: 'Templates',
        id: 'Templates',
        content: (
          <TemplatesForResourceDetailsTable
            items={templates}
            loading={loadingTemplates}
            userOwnsResource={userOwnsDataset()}
          />
        ),
        disabled: isGalaxi,
      },
      {
        label: 'Metadata',
        id: 'Metadata',
        content: (
          <MetadataDetails
            resourceOwnerIds={dataset?.Owners}
            activeWorkspace={props.activeWorkspace}
            resource={datasetArn}
            activeGroup={props.activeGroup}
            setContentType={props.setContentType}
            setNotification={notify}
          />
        ),
        disabled: isGalaxi,
      },
    ];

    if (signedInWithWorkspace(props)) {
      tabs.push({
        label: 'Tags',
        id: 'tags',
        content: (
          <DatasetTagList
            {...props}
            dataset={dataset}
            initialTags={tags}
            updateTagLabel={handleRefresh}
            notifyEditFailure={notifyFailure}
            notifyEditSuccess={notifySuccess}
          />
        ),
        disabled: isGalaxi,
      });

      tabs.push({
        label: 'Fine grain policies',
        id: 'fgaPolicies',
        content: <FineGrainPoliciesTable fgaPolicies={fgaPolicies} />,
        disabled: isGalaxi,
      });
    }

    if (isCommercialPartition()) {
      // insert at 4th position
      tabs.splice(3, 0, {
        label: 'Lineage',
        id: 'Lineage',
        disabled: !lineageEnabled,
        disabledReason:
          loadingBaseNodeId || loadingLineage ? 'Loading...' : 'Lineage information is not available for this dataset.',
        content: <LineageDetailV2 baseNodeId={lineageBaseNodeId} />,
        contentRenderStrategy: 'eager',
      });
    }

    if (userOwnsDataset()) {
      tabs.push({
        label: 'Consumers',
        id: 'consumers',
        content: getDataConsumersTable,
        disabled: isGalaxi,
      });
    }

    if (enableAdvisories()) {
      tabs.push({
        label: 'Advisories',
        id: 'advisories',
        content: (
          <AdvisoriesForResourceTable
            resourceArn={datasetArn}
            activeGroup={props.activeGroup}
            setContentType={props.setContentType}
          />
        ),
        disabled: isGalaxi,
      });
    }

    if (!signedInWithWorkspace(props) && dataset?.SupportedAccessTypes?.includes('IAM') && userOwnsDataset()) {
      tabs.push({
        label: 'IAM consumers',
        id: 'IAM consumers',
        content: (
          <IamConsumersTable
            dataset={dataset}
            datasetId={datasetId}
            activeGroup={props.activeGroup}
            onSuccess={notifySuccess}
            onFailure={notifyFailure}
          />
        ),
        disabled: isGalaxi,
      });
    }

    return <Tabs tabs={tabs} activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} />;
  };

  const dataQualityAvailable = () => {
    return ACCESS_PROVIDER_GALAXI != dataset?.AccessProvider && !loadingDataQuality && dataQualityMetrics?.length > 0;
  };

  const userOwnsDataset = () => {
    if (!dataset?.Owners || !props.userInfo) return false;
    if (isDGSAdmin(props.userInfo.memberGroupIds)) return true;
    // check if dataset owner is in user workspaces
    // authZ will happen on backend APIs, based on roles
    // Safely handle potentially undefined arrays with nullish coalescing
    const allOwnerIds = new Set([
      ...(props.userInfo.memberWorkspaceIds ?? []),
      ...(props.userInfo.memberGroupIds ?? []),
    ]);
    if (dataset.PrimaryOwner && allOwnerIds.has(dataset.PrimaryOwner)) {
      return true;
    }
    return dataset.Owners.some((owner) => allOwnerIds.has(owner));
  };

  const handleIamRequest = () => {
    const flattenedDataset = flattenItem(dataset, null, catalogName);
    let cartItem: CartAccessItem = {
      ...flattenedDataset,
      datasetName: flattenedDataset.tableName,
      catalogName: flattenedDataset.catalogDisplayName,
      owner: flattenedDataset.primaryOwner,
      permissionId: undefined,
      itemType: IAM_ACCESS_ITEM, // for IAM
      dataLakePrincipal: undefined,
      dataOwnerRole: undefined,
    };
    props.addToCart([cartItem]);
  };

  const handleLakeFormationRequest = async (e) => {
    if (e.detail.id == GALAXI_ACCESS_TYPE) {
      setSelectedPermissionType(GALAXI_TYPE);
      setAccessSelectVisible(true);
      return;
    }

    if (groupLevelDataPermissionList?.length !== 0) {
      const to: string =
        groupLevelDataPermissionList == undefined || groupLevelDataPermissionList?.length > 1
          ? Page.MY_DATASETS
          : createGroupPermissionLFLink(groupLevelDataPermissionList[0]?.dataPermissionId);
      props.setRedirect(to);
    }
    // otherwise, request access
    else {
      props.setRedirect(Page.CREATE_LAKE_FORMATION_PERMISSIONS, false, {
        tableInfo: { datasetId },
        tableName: idInfo.tableName,
        databaseName: idInfo.databaseName,
        catalogId: idInfo.catalogId,
        bucketName: idInfo.bucketName,
        region: idInfo.region,
        prevPage: createDatasetDetailLink(datasetId),
      });
    }
  };

  const headerButtons = useMemo(() => {
    let buttons = [];
    if (!props.allowListed) {
      if (isGalaxi) {
        buttons.push({
          text: SUBSCRIBE_TO_DATASET,
          onItemClick: () => {
            setSelectedPermissionType(GALAXI_TYPE);
            setAccessSelectVisible(true);
          },
          loading: getDatasetLoading,
        });
      } else {
        buttons.push({
          text: 'Onboard to Omni',
          loading: false,
          onItemClick: () => props.setRedirect(Page.ONBOARDING),
          variant: 'primary',
        });
      }
      return buttons;
    }

    if (userOwnsDataset() || isDataLakeAdmin(props.activeGroup)) {
      buttons.push({
        text: 'Manage',
        loading: getDatasetLoading || syncLoading || editLoading,
        disabled: dataset?.TableState == TABLE_STATUS_DEPRECATED,
        onItemClick: (e) => {
          if (e.detail.id == 'edit') {
            setEditDataSetModalVisible(true);
          } else if (e.detail.id == 'sync') {
            syncDataset();
          }
        },
        items: [
          { text: 'Edit', id: 'edit', disabled: isGalaxi },
          { text: 'Sync dataset', id: 'sync' },
        ],
      });
    }

    if (dataset?.SupportedAccessTypes?.includes('IAM')) {
      if (!signedInWithWorkspace(props)) {
        buttons.push({
          text: 'Subscribe to IAM data',
          loading: getDatasetLoading,
          onItemClick: handleIamRequest,
          disabled: props.cartItemIds.includes(datasetId),
          id: 'IAM',
        });
      }
      return buttons;
    }

    if (dataset?.DataSourceType?.includes('DZ')) {
      if (signedInWithWorkspace(props)) {
        buttons.push({
          text: SUBSCRIBE_TO_DATASET,
          onItemClick: () => {
            setSelectedPermissionType(DATAZONE_TYPE);
            setAccessSelectVisible(true);
          },
          disabled: alreadyHasDataZoneAccess,
          loading: getDatasetLoading,
        });
      }
      return buttons;
    }

    if (dataset?.SupportedAccessTypes?.includes('Redshift')) {
      if (signedInWithWorkspace(props)) {
        buttons.push({
          text: SUBSCRIBE_TO_DATASET,
          onItemClick: () => {
            setSelectedPermissionType(TAG_ITEM);
            setAccessSelectVisible(true);
          },
          disabled: props.activeWorkspace == null || dataset?.TableState == TABLE_STATUS_DEPRECATED,
          loading: getDatasetLoading,
        });
      }
      return buttons;
    }

    if (isGalaxi) {
      buttons.push({
        text: SUBSCRIBE_TO_DATASET,
        onItemClick: () => {
          setSelectedPermissionType(GALAXI_TYPE);
          setAccessSelectVisible(true);
        },
        loading: getDatasetLoading,
      });
      return buttons;
    }

    if (dataset?.SupportedAccessTypes?.includes('LakeFormation')) {
      if (signedInWithWorkspace(props)) {
        buttons.push({
          text: SUBSCRIBE_TO_DATASET,
          onItemClick: openAccessSelectModal,
          items: [
            resourceAccessAvailable
              ? {
                  text: 'Resource access',
                  id: RESOURCE_ITEM,
                }
              : undefined,
            fgaAccessAvailable
              ? {
                  text: 'Fine grain policies access',
                  id: FGA_POLICY_TYPE_PREFIX,
                }
              : undefined,
            tagAccessAvailable
              ? {
                  text: 'Tag access',
                  id: TAG_ITEM,
                }
              : undefined,
          ].filter((item) => !!item),
          disabled:
            dataset?.TableState == TABLE_STATUS_DEPRECATED ||
            (!resourceAccessAvailable && !fgaAccessAvailable && !tagAccessAvailable && !galaxiAccessAvailable),
          loading: !resourceAccessAvailable && !fgaAccessAvailable && !tagAccessAvailable && !galaxiAccessAvailable,
        });
        return buttons;
      } else {
        const lakeFormationAccess = !(
          groupLevelDataPermissionList === undefined || groupLevelDataPermissionList.length === 0
        );

        if (galaxiServiceIntegrated() && (props.galaxiGroups == null || props.galaxiGroups.length > 0)) {
          buttons.push({
            text: SUBSCRIBE_TO_DATASET,
            loading: getDatasetLoading,
            onItemClick: (e) => handleLakeFormationRequest(e),
            items: [
              {
                text: 'Omni access',
                disabled: lakeFormationAccess || dataset?.TableState == TABLE_STATUS_DEPRECATED,
                variant: 'normal',
                id: 'LakeFormation',
              },
              {
                text: 'View dataset permission',
                disabled: !lakeFormationAccess,
                variant: 'normal',
                id: 'LakeFormation',
              },
            ],
          });
        } else {
          if (lakeFormationAccess) {
            buttons.push({
              text: 'View dataset permission',
              loading: getDatasetLoading,
              onItemClick: (e) => handleLakeFormationRequest(e),
              variant: 'normal',
              id: 'LakeFormation',
            });
          }
          if (!lakeFormationAccess && TABLE_STATUS_DEPRECATED != dataset?.TableState) {
            buttons.push({
              text: SUBSCRIBE_TO_DATASET,
              loading: getDatasetLoading,
              onItemClick: (e) => handleLakeFormationRequest(e),
              variant: 'normal',
              id: 'LakeFormation',
            });
          }
        }
        return buttons;
      }
    }
    return [];
  }, [
    dataset,
    getDatasetLoading,
    syncLoading,
    editLoading,
    permissionDropdownOptions,
    resourceAccessAvailable,
    fgaAccessAvailable,
    tagAccessAvailable,
    galaxiAccessAvailable,
  ]);

  if (getDatasetFailed) {
    return (
      <ResourceNotFound
        title={'Failed to load dataset'}
        subtitle={<>There was an error when loading the dataset. Please contact {getOmniOncallLink()} for assistance</>}
      />
    );
  }

  if (!dataset && !getDatasetLoading) {
    return (
      <ResourceNotFound
        title={'Dataset not found'}
        subtitle={
          <>
            The given dataset is not valid, is hidden or you do not have permission to view it. Please check the URL for
            mistakes and try again. If you require assistance contact {getOmniOncallLink()}.
          </>
        }
      />
    );
  }

  if (!dataset) {
    return <Spinner />;
  }

  return (
    <SpaceBetween size={'s'} direction={'vertical'}>
      {notifications?.length > 0 && <Flashbar items={notifications} />}
      {!!dataset && (
        <DetailsPageHeader
          dataset={dataset}
          tags={tags}
          businessGlossaries={businessGlossaries}
          governanceTags={governanceTags}
          hasLineage={lineageEnabled}
          hasDataQuality={dataQualityAvailable()}
          header={
            <SpaceBetween size={'s'} direction={'horizontal'} alignItems={'center'}>
              {idInfo.clusterId == undefined ? lakeFormationIcon : redshiftIcon}
              {dataset?.DataSetName}
            </SpaceBetween>
          }
          notifyFailure={notifyFailure}
          buttons={headerButtons}
          cti={CTI}
          setMostRecentVersionOption={setMostRecentOption}
          selectedVersionOption={option}
          setSelectedVersionOption={setOption}
        />
      )}
      {detailPageTabs()}
      {editDataSetModalVisible && (
        <DatasetEditModal
          {...props}
          visible={editDataSetModalVisible}
          dismiss={() => setEditDataSetModalVisible(false)}
          dataset={dataset}
          notifyEditFailure={notifyFailure}
          notifyEditSuccess={notifySuccess}
          setEditLoading={setEditLoading}
          refreshPage={fetchDataset}
        />
      )}
      {accessSelectVisible && catalogName && dataset && selectedPermissionType && permissionDropdownOptions && (
        <AccessSelectModal
          {...props}
          visible={accessSelectVisible}
          loading={false} // TODO: enhance this later? It might not be needed anymore.
          dismiss={() => {
            setAccessSelectVisible(false);
          }}
          addToCart={props.addToCart}
          dataset={dataset}
          catalogName={catalogName}
          selectedPermissionType={selectedPermissionType}
          allPermissionDropdownOptions={permissionDropdownOptions}
        />
      )}
    </SpaceBetween>
  );
};
